import { Box, Typography, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import nycExposureWebp from '../static/nyc_exposure_stop_0.25_q70.webp';
import { Fade } from '@mui/material';


// Define color schemes for different card styles using brand colors
// Each scheme has a progressively darker base color with subtle top-left to bottom-right darkening
export const COLOR_SCHEMES = {  
    light: {
        // Lightest base: subtle brand tint with barely-perceptible gradient
        background: 'linear-gradient(180deg, color-mix(in srgb, var(--brand-main) 52%, white) 0%, color-mix(in srgb, var(--brand-main) 48%, white) 100%)',
        headerBackground: 'transparent',
        textColor: '#555555',
        buttonBackground: 'var(--brand-main)',
        buttonText: '#0D0D0D',
        borderColor: 'rgba(0, 0, 0, 0.15)',
        boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.2), 0 4px 12px rgba(0, 0, 0, 0.5), 0 8px 32px rgba(0, 0, 0, 0.3)',
        border: '1px solid rgba(79, 234, 255, 0.35)',
        scrollbarTrack: 'rgba(0, 0, 0, 0.1)',
        scrollbarThumb: 'rgba(0, 0, 0, 0.3)',
    },
    medium: {
        // Medium base: subtle darkening gradient (85% to 80%)
        background: 'linear-gradient(180deg, color-mix(in srgb, var(--brand-secondary) 87%, black) 0%, color-mix(in srgb, var(--brand-secondary) 83%, black) 100%)',
        headerBackground: 'transparent',
        textColor: '#444444',
        buttonBackground: 'var(--brand-main)',
        buttonText: '#0D0D0D',
        borderColor: 'rgba(0, 0, 0, 0.15)',
        boxShadow: 'inset 0 1px 0 rgba(79, 234, 255, 0.3), 0 4px 12px rgba(0, 0, 0, 0.6), 0 8px 32px rgba(0, 0, 0, 0.4)',
        border: '1px solid rgba(9, 197, 220, 0.4)',
        scrollbarTrack: 'rgba(0, 0, 0, 0.15)',
        scrollbarThumb: 'rgba(0, 0, 0, 0.35)',
    },
    dark: {
        // Darkest base: refined gradient (60% to 55%)
        background: 'linear-gradient(180deg, color-mix(in srgb, var(--brand-secondary) 62%, black) 0%, color-mix(in srgb, var(--brand-secondary) 58%, black) 100%)',
        headerBackground: 'transparent',
        textColor: 'white',
        buttonBackground: 'var(--brand-main)',
        buttonText: '#0D0D0D',
        borderColor: 'rgba(255, 255, 255, 0.2)',
        boxShadow: 'inset 0 1px 0 rgba(79, 234, 255, 0.4), 0 4px 12px rgba(0, 0, 0, 0.7), 0 8px 32px rgba(0, 0, 0, 0.5)',
        border: '1px solid rgba(79, 234, 255, 0.35)',
        scrollbarTrack: 'rgba(255, 255, 255, 0.1)',
        scrollbarThumb: 'rgba(255, 255, 255, 0.3)',
    },
};

export function ProductCard({
        productName, 
        description, 
        features_list, 
        price, 
        selectedPrice=null,
        cta, 
        optionalDescription=null,
        colorScheme = COLOR_SCHEMES.light,
        sx={},
        isSelected=false
    }) {
        const navigate = useNavigate();
        return (
            <Fade in={true} timeout={1000}>
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                background: colorScheme.background,
                borderRadius: '14px',
                boxShadow: colorScheme.boxShadow,
                border: colorScheme.border,
                width: { xs: '90%', sm: '55%', md: '35%', lg: '28%', xl: '25%' }, 
                height: { xs: 'auto', sm: 'auto', md: '80%', xl: '70%' },
                minHeight: { xs: '500px', sm: '550px', md: '90%', xl: '800px' },
                maxWidth: { xs: '90%', sm: '70%', md: '35%', lg: '28%', xl: '450px' },                        
                ...sx,
            }}>
                {/* Product Name Section - Fixed Height */}
                <Box sx={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '130px',
                    marginBottom: 2,
                    borderBottom: `1px solid ${colorScheme.borderColor}`,
                    borderTopLeftRadius: '24px',
                    borderTopRightRadius: '24px',
                    backgroundColor: colorScheme.headerBackground,
                    paddingTop: 3,
                    paddingBottom: 3,
                }}>
                    {/* X button */}
                    {isSelected && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 12,
                                right: 16,
                                zIndex: 2,
                                cursor: 'pointer',
                                color: colorScheme.textColor,
                                bgcolor: 'rgba(255,255,255,0.08)',
                                borderRadius: '50%',
                                width: 32,
                                height: 32,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'background 0.15s',
                                '&:hover': {
                                    backgroundColor: 'rgba(80,80,80,0.16)'
                                }
                            }}
                            onClick={() => navigate('/pricing')}
                            aria-label="Close"
                        >
                            <Typography variant="h6" sx={{fontWeight: 900, fontSize: 17.6, lineHeight: 1}}>×</Typography>
                        </Box>
                    )}
                    <Typography variant="h1" sx={{
                        fontSize: { xs: '2.4rem', xl: '2.8rem' },
                        fontWeight: 600,
                        color: colorScheme.textColor,
                        textAlign: 'center',
                    }}>
                        {productName}
                    </Typography>
                
                    <Typography variant="body1" sx={{
                        fontSize: { xs: '0.72rem', sm: '0.8rem', md: '0.88rem', lg: '0.96rem' },
                        color: colorScheme.textColor,
                        WebkitFontSmoothing: 'antialiased',                    
                        textAlign: 'center',
                    }}>
                        {description}
                    </Typography>
                </Box>
                {optionalDescription && (      
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: 1,
                        minHeight: '50px',
                    }}>
                        
                            <Typography variant="body1" sx={{
                                fontSize: { xs: '0.8rem', sm: '0.88rem', md: '0.92rem', lg: '1rem' },
                                fontWeight: 800,
                                color: colorScheme.textColor,
                                WebkitFontSmoothing: 'antialiased',                    
                                textAlign: 'center',
                            }}>
                                {optionalDescription}   
                            </Typography>
                        
                    </Box>
                )}
                <Box sx={{
                    flex: '1 1 auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    marginBottom: 3,
                    overflowY: 'auto',
                    paddingLeft: 3,
                    paddingRight: 2.5,
                    '&::-webkit-scrollbar': {
                        width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: colorScheme.scrollbarTrack,
                        borderRadius: '3px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: colorScheme.scrollbarThumb,
                        borderRadius: '3px',
                    },
                }}>
                    {features_list.map((feature, index) => (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            justifyContent: 'center',           
                        }}>

                            <Typography variant="h6" sx={{
                                fontSize: { xs: '0.76rem', sm: '0.8rem', md: '0.84rem', lg: '0.88rem' }, 
                                fontWeight: 800,
                                color: colorScheme.textColor,
                                WebkitFontSmoothing: 'antialiased',                                                
                                my: 0.5,
                            }}>
                                {feature.name}
                            </Typography>
                            
                            <Typography variant="body1" sx={{
                                fontSize: { xs: '0.68rem', sm: '0.72rem', md: '0.76rem', lg: '0.8rem' },
                                fontWeight: 400,
                                color: colorScheme.textColor,
                                WebkitFontSmoothing: 'antialiased',                        
                            }}>
                                {feature.value.map((item, index) => (
                                    <div key={index} style={{ display: 'flex', alignItems: 'flex-start' }}>
                                        {item}
                                    </div>
                                ))}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                {/* Price Section - Fixed Height */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '50px',
                    marginBottom: 2.5,
                    paddingLeft: 3,
                    paddingRight: 3,
                    
                }}>
                    {isSelected ? selectedPrice : price}
                </Box>

                {/* CTA Button Section - Fixed Height */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '60px',
                    paddingLeft: 3,
                    paddingRight: 3,
                    paddingBottom: 3,
                }}>
                    {
                    isSelected ? null : 
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                            <Button
                                variant="contained"
                                sx={{
                                    backgroundColor: COLOR_SCHEMES.light.buttonBackground,
                                    color: COLOR_SCHEMES.light.buttonText,
                                    textTransform: 'none',
                                    borderRadius: '50px',
                                    fontWeight: '600',
                                    padding: { xs: '10px 24px', sm: '11px 28px', md: '12px 32px' },
                                    fontSize: { xs: '0.72rem', sm: '0.76rem', md: '0.8rem' },
                                    '&:hover': {
                                        backgroundColor: COLOR_SCHEMES.light.buttonBackground,
                                        opacity: 0.9,
                                    },
                                }}
                                onClick={() => {
                                    navigate(cta.link);
                                }}
                            >
                                {cta.buttonText}
                            </Button>
                            <Typography variant="body1" sx={{
                                fontSize: { xs: '0.68rem', sm: '0.72rem', md: '0.8rem' },
                                color: COLOR_SCHEMES.light.textColor,                    
                                textAlign: 'center',
                                marginTop: 1,
                            }}>
                                {cta.msg}
                            </Typography>
                        </Box>  
                    }
                </Box>
            </Box>
            </Fade>
        );
    }

export const products = [
    {
        name: 'Pro',
        description: 'For individuals & smaller teams',
        features: [
            { 
                name: 'U.S. Coverage', 
                value: [
                    'S&P 500, 400, and 600 companies', 
                ]
            },
            {
                name: 'deepKPI Database',
                value: [
                    '10 years of KPIs sourced from 10-K, 10-Q, 8-Ks',
                    "Excel add-in seamlessly integrates with your models",
                    'Unlimited data pulls, offline usage permitted'
                ], 
            },
            {
                name: 'deepAnalysis AI',
                value: [
                    "Fast facts from deepKPI, earnings calls & filings",
                    "AI report drafting with inline citations to facts & figures",                        
                    "500 queries per month"
                ]
            },
            {
                name: 'deepInsight Library',
                value: [
                    'Institution-grade models & research reports',
                    'Unlimited downloads',
                ]
            },                
        ],
        price: (
            <Box sx={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'center',
            }}>
                <Typography variant="body1" sx={{
                    fontSize: '2.4rem',
                    fontWeight: 400,
                    color: COLOR_SCHEMES.light.textColor,
                }}>
                $99
                </Typography>
                <Typography component="span" sx={{ fontSize: '1.2rem', fontWeight: 400, marginLeft: 0.5, color: COLOR_SCHEMES.light.textColor }}>
                    /month
                </Typography>
            </Box>  
        ),
        selectedPrice: (
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
            }}>
                <Typography variant="body1" sx={{
                    fontSize: '1.6rem',
                    fontWeight: 400,
                    color: COLOR_SCHEMES.light.textColor,
                }}>
                14-day Free Trial
                </Typography>
                <Typography component="span" sx={{ fontSize: '0.8rem', fontWeight: 400, marginLeft: 0.5, color: COLOR_SCHEMES.light.textColor }}>
                    No credit card required
                </Typography>
            </Box>  
        ),
        cta: {
            link: '/signup',
            buttonText: 'Start Free Trial',
            msg: 'No credit card required',
        },
        colorScheme: COLOR_SCHEMES.light,
    }, 
    {
        name: 'Enterprise',
        description: <>For larger firms & institutions</>,
        optionalDescription: "Everything in Pro, plus:",
        features: [                
            {
                name: "Global coverage",
                value: [
                    "U.S., Singapore & Hong Kong listed companies",
                    "Priority access to new geos",
                ]
            }, 
            {
                name: "Platform Upgrades",
                value: [
                    "Unlimited deepAnalysis AI queries",
                    "Priority requests for deepInsight coverage initiation",
                ]
            },
            {
                name: "Enterprise Features",
                value: [
                    "Custom, private cloud, or on-prem deployment options",
                    "Enterprise-grade security & compliance",
                    "Custom API endpoint options",
                ]
            },
            {
                name: "Priority Support",
                value: [
                    "White-glove onboarding & training",
                    "24-hr SLA for support requests",
                    "Priority access to Revelata team",
                ]
            }
        ],
        price: (
            <Typography variant="body1" sx={{
                fontSize: '1.2rem',
                fontWeight: 600,
                color: COLOR_SCHEMES.dark.textColor,
                textAlign: 'center',
            }}>
                Let's discuss your needs
            </Typography>
        ),
        selectedPrice: (
            <Typography variant="body1" sx={{
                fontSize: '1.2rem',
                fontWeight: 600,
                color: COLOR_SCHEMES.dark.textColor,
                textAlign: 'center',
            }}>
                Let's discuss your needs
            </Typography>
        ),
        cta: {
            link: '/inquiry',
            buttonText: 'Contact Us',
            msg: <>&nbsp;</>
        },
        colorScheme: COLOR_SCHEMES.dark,
    },
]


export function PricingPage() {
    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            width: '100%',
            height: { xs: 'auto', sm: 'auto', md: '100%' },
            minHeight: { xs: '100vh', sm: '100vh', md: '100%' },
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            backgroundImage: `linear-gradient(rgba(0,0,0,0.45), rgba(0,0,0,0.45)), url(${nycExposureWebp})`,
            boxSizing: 'border-box',
        }}>            
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'column', md: 'row' },
                alignItems: 'center',
                justifyContent: { xs: 'flex-start', sm: 'flex-start', md: 'center' },
                width: { xs: '100%', sm: '100%', md: '100%' },
                height: 'auto',
                gap: { xs: '20px', sm: '20px', md: '5%' },
                paddingLeft: { xs: '20px', sm: '20px', md: 0 },
                paddingRight: { xs: '20px', sm: '20px', md: 0 },
                py: 'auto',
                my: 'auto',
                overflowY: 'visible',
            }}>
            {products.map((product) => (
                <ProductCard 
                    key={product.name} 
                    productName={product.name} 
                    description={product.description} 
                    features_list={product.features} 
                    price={product.price} 
                    cta={product.cta}
                    optionalDescription={product.optionalDescription}
                    colorScheme={product.colorScheme}
                    
                />
            ))}
            </Box>            
        </Box>
    );
}

export default PricingPage;